import { db } from "firebase.js"; // Firestore instance
import {
  updateDoc,
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { updateUserRole } from "./userService";
import * as Sentry from "@sentry/react";

// Tournament images mapped by ID
import tapisVertImage from "assets/images/tapis_vert.jpeg";
import maBrasserieImage from "assets/images/ma_brasserie.jpg";
import bromontImage from "assets/images/bromont.jpg";
import fitzroyImage from "assets/images/fitzroy.jpg";
import tornadoMontrealImage from "assets/images/tornado_mtl.jpg";
import doubleMentonImage from "assets/images/double_menton.jpg";
import RegistrationLimit from "Components/Tournament/RegistrationLimit";

const tournamentImages = {
  "tapis-vert": tapisVertImage,
  "ma-brasserie": maBrasserieImage,
  bromont: bromontImage,
  fitzroy: fitzroyImage,
  "brasserie-champlain": tornadoMontrealImage,
  "test-location": tornadoMontrealImage,
  "double-menton": doubleMentonImage,
};

const REGISTRATION_HARD_LIMIT = 100;

/**
 * Toggle tournament registration status (Admin Only)
 * @param {string} tournamentId - The tournament's ID
 * @param {boolean} currentStatus - Current status of registrationClosed
 */
export const toggleRegistrationClosed = async (tournamentId, currentStatus) => {
  const tournamentRef = doc(db, "tournaments", tournamentId);
  const newStatus = !currentStatus;

  try {
    await updateDoc(tournamentRef, { registrationClosed: newStatus });
    return newStatus;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error updating tournament:", error);
    throw error;
  }
};

export const canManage = (user, locationId) => {
  if (!user) return false; // No user, no access
  if (user.master) return true; // Masters always have access
  if (user.adminFor === locationId) return true; // Admins have access
  return false; // Otherwise, no access
};

export const updateTournamentLimits = async (tournamentId, limits) => {
  const tournamentRef = doc(db, "tournaments", tournamentId);
  await updateDoc(tournamentRef, limits);
};

// 🔹 Fetch a single tournament
export const fetchTournamentById = async (tournamentId) => {
  const docRef = doc(db, "tournaments", tournamentId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) return null;

  return {
    id: snapshot.id,
    ...snapshot.data(),
    image: tournamentImages[snapshot.id] || "https://via.placeholder.com/600",
  };
};

// 🔹 Subscribe to registrations for a tournament
export const subscribeToTournamentRegistrations = (tournamentId, onUpdate) => {
  const q = query(
    collection(db, "registrations"),
    where("tournamentId", "==", tournamentId),
    where("discarded", "==", false),
    orderBy("createdAt", "asc")
  );

  return onSnapshot(q, (snapshot) => {
    const registrations = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log(
      `[Firestore READS] Registrations snapshot (${snapshot.docChanges().length} changes, ${snapshot.size} total docs)`
    );

    onUpdate(registrations);
  });
};

// 🔹 Fetch all active tournaments (with real-time updates)
export const subscribeToActiveTournaments = (onUpdate) => {
  const q = query(collection(db, "tournaments"), where("active", "==", true));

  return onSnapshot(q, (snapshot) => {
    const tournaments = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    onUpdate(tournaments);
  });
};

export const createTournament = async (tournamentData) => {
  // Create slug-like ID from name
  const randomSuffix = Math.random().toString(36).substring(2, 8); // generates 6-char alphanumeric
  const tournamentId =
  `${tournamentData.locationId}-${tournamentData.type.replace(/\s+/g, '-')}-${randomSuffix}`.toLowerCase();

  const tournamentRef = doc(db, "tournaments", tournamentId);

  // Optional: Check if ID already exists
  const snapshot = await getDoc(tournamentRef);
  if (snapshot.exists()) {
    throw new Error("Un tournoi avec ce nom existe déjà.");
  }

  // Add createdAt field
  const tournamentWithTimestamp = {
    ...tournamentData,
    RegistrationLimit: REGISTRATION_HARD_LIMIT,
    createdAt: serverTimestamp(),
  };

  await setDoc(tournamentRef, tournamentWithTimestamp);

  return tournamentId;
};

export const updateTournament = async (tournamentId, tournamentData) => {
  const tournamentRef = doc(db, "tournaments", tournamentId);
  await updateDoc(tournamentRef, tournamentData);
};

export const archiveTournament = async (tournamentId) => {
  const tournamentRef = doc(db, "tournaments", tournamentId);
  await updateDoc(tournamentRef, {
    discardedAt: serverTimestamp(),
  });
};

export const deleteTournament = async (tournamentId) => {
  // 1. Fetch registrations linked to the tournament
  const registrationsRef = collection(db, "registrations");
  const q = query(registrationsRef, where("tournamentId", "==", tournamentId));
  const snapshot = await getDocs(q);

  // 2. Delete each registration document
  const deletePromises = snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref));
  await Promise.all(deletePromises);

  // 3. Delete the tournament itself
  const tournamentRef = doc(db, "tournaments", tournamentId);
  await deleteDoc(tournamentRef);
};


export const fetchTournamentsByLocation = async (locationId) => {
  const q = query(
    collection(db, "tournaments"),
    where("locationId", "==", locationId),
    where("discardedAt", "==", null)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      image: tournamentImages[locationId] || "https://via.placeholder.com/600",
    };
  });
};

export const fetchArchivedTournamentsByLocation = async (locationId) => {
  const q = query(
    collection(db, "tournaments"),
    where("locationId", "==", locationId),
    where("discardedAt", "!=", null)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      image: tournamentImages[locationId] || "https://via.placeholder.com/600",
    };
  });
};

export const updateTournamentPodiums = async (tournamentId, podiums) => {
  const tournamentRef = doc(db, "tournaments", tournamentId);
  await updateDoc(tournamentRef, { podiums });
};

export const updateTournamentTeams = async (tournamentId, teams) => {
  try {
    await updateDoc(doc(db, "tournaments", tournamentId), {teams});
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error updating tournament teams:", error);
    throw error;
  }
};

export const fetchArchivedTournaments = async (locationId) => {
  const tournamentsRef = collection(db, 'tournaments');
  const q = query(
    tournamentsRef,
    where('locationId', '==', locationId),
    where('discardedAt', '!=', null)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// 🔹 Helpers
const getRankedRegistrations = (registrations, locationRankings) => {
  const regMap = new Map();
  const teamMateNameMap = new Map();
  const allIds = new Set();

  // Build maps for user data
  registrations.forEach((reg) => {
    regMap.set(reg.userId, reg);
    allIds.add(reg.userId);
    if (reg.teamMateId) {
      allIds.add(reg.teamMateId);
      if (reg.teamMateName) {
        teamMateNameMap.set(reg.teamMateId, reg.teamMateName);
      }
    }
  });

  const enrichedUsers = Array.from(allIds).map((userId) => {
    const base = regMap.get(userId);
    const rankingObj = locationRankings.find((lr) => lr.userId === userId);

    return {
      userId,
      name: base?.name || teamMateNameMap.get(userId) || userId,
      ranking: rankingObj?.ranking ?? 0,
      ...(base || {}),
    };
  });

  return enrichedUsers.sort((a, b) => b.ranking - a.ranking);
};




const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const makeShuffledPairs = (players) => {
  const sorted = [...players].sort((a, b) => b.ranking - a.ranking);
  const half = Math.floor(sorted.length / 2);
  const topHalf = shuffle(sorted.slice(0, half));
  const bottomHalf = shuffle(sorted.slice(half));

  const teams = [];

  for (let i = 0; i < topHalf.length; i++) {
    const topPlayer = topHalf[i];
    const bottomPlayer = bottomHalf[i];
    teams.push({
      players: [topPlayer, bottomPlayer],
      totalRanking: (topPlayer?.ranking ?? 0) + (bottomPlayer?.ranking ?? 0),
      teamNumber: i + 1,
    });
  }

  return teams;
};


const seedTeamsIntoDivisions = (teams) => {
  const division1 = [];
  const division2 = [];
  teams.forEach((team, index) => {
    const mod = index % 4;
    if (mod === 0 || mod === 3) {
      division1.push({ ...team, division: "division1" });
    } else {
      division2.push({ ...team, division: "division2" });
    }
  });
  return { division1, division2 };
};

const createTeams = (players, type) => {
  if (type === "Simple") {
    return players.map((player) => ({
      players: [player],
      totalRanking: player.ranking ?? 0,
    }));
  } else {
    return makeShuffledPairs(players);
  }
};

const assignTeamsToDivisions = (sortedTeams, division1TeamsCount, division2TeamsCount, makeDivisions) => {
  if (division2TeamsCount === 0) {
    return {
      division1: sortedTeams.map((team, index) => ({
        ...team,
        division: "division1",
        teamNumber: index + 1,
      })),
    };
  }

  if (!makeDivisions) {
    return seedTeamsIntoDivisions(sortedTeams);
  }

  return {
    division1: sortedTeams.slice(0, division1TeamsCount).map((team) => ({
      ...team,
      division: "division1",
    })),
    division2: sortedTeams.slice(division1TeamsCount).map((team) => ({
      ...team,
      division: "division2",
    })),
  };
};

// 🔸 Main Function
export const generateTournamentTeams = async ({
  tournamentId,
  registrations,
  locationRankings,
  division1TeamsCount,
  division2TeamsCount,
}) => {
  // tournament is refetched here to make sure we have the latest data
  const tournament = await fetchTournamentById(tournamentId);

  if (tournament.type === "Double Open") {
    await generateOpenDoublesTeams(
      tournament,
      registrations,
      locationRankings,
      division1TeamsCount,
      division2TeamsCount,
    );
    return;
  }
  const waitingListLimit = tournament.waitingListLimit ?? Infinity;
  const playersPerTeam = tournament.type === "Simple" ? 1 : 2;

  const cappedRegistrations = getRankedRegistrations(
    registrations.slice(0, waitingListLimit),
    locationRankings
  );

  let totalTeams = division1TeamsCount + division2TeamsCount;
  const totalPlayers = totalTeams * playersPerTeam;

  if (cappedRegistrations.length < totalPlayers) {
    throw new Error(
      `Pas assez de joueurs pour ${totalTeams} équipes (${totalPlayers} joueurs nécessaires).`
    );
  }

  const rankedPlayers = cappedRegistrations.slice(0, totalPlayers);

  let allTeams;

  if (tournament.type === "DYP" && tournament.makeDivisions && division2TeamsCount > 0) {
    const half = division1TeamsCount * 2;
    const topHalf = rankedPlayers.slice(0, half);
    const bottomHalf = rankedPlayers.slice(half);
  
    let division1 = makeShuffledPairs(topHalf);
    let division2 = makeShuffledPairs(bottomHalf);
  
    division1 = division1
      .sort((a, b) => b.totalRanking - a.totalRanking)
      .map((team, i) => ({
        ...team,
        division: "division1",
        teamNumber: i + 1,
      }));
  
    division2 = division2
      .sort((a, b) => b.totalRanking - a.totalRanking)
      .map((team, i) => ({
        ...team,
        division: "division2",
        teamNumber: i + 1,
      }));
  
    await updateTournamentTeams(tournament.id, { division1, division2 });
    return;
  }

  allTeams = createTeams(rankedPlayers, tournament.type);

  const sortedTeams = allTeams
    .sort((a, b) => b.totalRanking - a.totalRanking)
    .map((team, index) => ({ ...team, teamNumber: index + 1 }));

  const { division1, division2 } = assignTeamsToDivisions(
    sortedTeams,
    division1TeamsCount,
    division2TeamsCount,
    tournament.makeDivisions
  );

  const result = { division1 };
  if (division2?.length) result.division2 = division2;

  await updateTournamentTeams(tournament.id, result);
};

const generateOpenDoublesTeams = async (
  tournament,
  registrations,
  locationRankings,
  division1TeamsCount,
  division2TeamsCount,
) => {
  // ✅ Apply rankings to ALL registrations first
  const allRankedRegistrations = getRankedRegistrations(
    registrations,
    locationRankings
  );

  const regMap = new Map();
  allRankedRegistrations.forEach((reg) => {
    regMap.set(reg.userId, reg);
  });

  const used = new Set();
  const teams = [];

  for (const reg of allRankedRegistrations) {
    if (used.has(reg.userId) || !reg.teamMateId) continue;

    const mate = regMap.get(reg.teamMateId);
    if (!mate || used.has(mate.userId)) continue;

    used.add(reg.userId);
    used.add(mate.userId);

    teams.push({
      players: [reg, mate],
      totalRanking: (reg.ranking ?? 0) + (mate.ranking ?? 0),
    });
  }

  const totalTeams = division1TeamsCount + division2TeamsCount;

  // ✅ Slice teams *after* they're built
  const selectedTeams = teams
    .sort((a, b) => b.totalRanking - a.totalRanking)
    .slice(0, totalTeams)
    .map((team, i) => ({
      ...team,
      teamNumber: i + 1,
    }));

  if (selectedTeams.length < totalTeams) {
    throw new Error(
      `Pas assez d'équipes valides pour ${totalTeams} slots. (${selectedTeams.length} équipes trouvées)`
    );
  }

  const { division1, division2 } = assignTeamsToDivisions(
    selectedTeams,
    division1TeamsCount,
    division2TeamsCount,
    tournament.makeDivisions
  );

  const result = { division1 };
  if (division2?.length) result.division2 = division2;

  await updateTournamentTeams(tournament.id, result);
};







